import { useEffect, useState } from 'react';
import { Button, Form, Nav } from 'react-bootstrap';
import {
  updateModelProfileService,
  uploadModelCoverService,
  fetchModelMediaService,
  uploadModelMediaService,
  updateModelMediaService,
  deleteModelMediaService,
} from '../../services/publicModelsService';
import { STORAGE } from '../../utils/headers';
import noimg from '../../assets/img/noimg.jpg';
import './model-profile-manager.css';

const EMPTY_PROFILE = {
  slug: '',
  stage_name: '',
  tagline: '',
  gender: '',
  age: '',
  description: '',
  preferences: '',
  country: '',
  city: '',
  languages: '',
  is_public: true,
};

const asset = (path) => (path ? `${STORAGE}/${path}` : noimg);

/**
 * Edición del perfil público de una modelo: textos de presentación,
 * preferencias y material promocional (fotos y videos cortos).
 *
 * Es el formulario a secas, sin marco. Lo usan dos sitios con necesidades
 * distintas: el panel de administración lo abre en un modal sobre el listado
 * (<ModelProfileManager />) y la modelo lo tiene como pantalla de su propio
 * panel. Estaba soldado al modal, así que la modelo no tenía forma de llegar
 * a él salvo entrando a una pantalla de administración que no le corresponde.
 *
 * `active` indica si está a la vista: el modal lo monta aunque esté cerrado y
 * no debe volcar el formulario ni pedir la galería hasta que se abre.
 */
export default function ModelProfileEditor({ model, onSaved, active = true }) {
  const [tab, setTab] = useState('profile');
  const [form, setForm] = useState(EMPTY_PROFILE);
  const [media, setMedia] = useState([]);
  const [status, setStatus] = useState({ type: '', message: '' });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [cover, setCover] = useState(null);
  const [uploadingCover, setUploadingCover] = useState(false);

  // Se vuelca al abrir o al cambiar de modelo, no cada vez que llegan
  // datos nuevos de la misma: si dependiera del objeto entero, guardar
  // refrescaría el formulario y borraría el aviso de "guardado correctamente"
  // en el mismo instante en que aparece.
  useEffect(() => {
    if (!active || !model) return;

    setTab('profile');
    setStatus({ type: '', message: '' });
    setForm({
      slug: model.slug || '',
      stage_name: model.stage_name || '',
      tagline: model.tagline || '',
      gender: model.gender || '',
      age: model.age || '',
      description: model.description || '',
      preferences: model.preferences || '',
      country: model.country || '',
      city: model.city || '',
      languages: model.languages || '',
      is_public: model.is_public !== false,
    });

    setCover(model.cover_img || null);

    fetchModelMediaService({ modelId: model.id })
      .then((result) => setMedia(result || []))
      .catch(() => setMedia([]));
  }, [active, model?.id]);

  const change = (name, value) => setForm((prev) => ({ ...prev, [name]: value }));

  const saveProfile = async (event) => {
    event.preventDefault();
    setSaving(true);
    setStatus({ type: '', message: '' });
    try {
      const payload = { ...form, age: form.age === '' ? null : Number(form.age) };
      const updated = await updateModelProfileService(model.id, payload);
      setStatus({ type: 'success', message: 'Perfil actualizado correctamente.' });
      if (onSaved) onSaved(updated);
    } catch (error) {
      setStatus({ type: 'error', message: error?.message || 'No fue posible guardar el perfil.' });
    } finally {
      setSaving(false);
    }
  };

  const handleCover = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    setUploadingCover(true);
    setStatus({ type: '', message: '' });
    try {
      const actualizado = await uploadModelCoverService(model.id, file);
      setCover(actualizado.cover_img);
      setStatus({ type: 'success', message: 'Portada actualizada.' });
      if (onSaved) onSaved(actualizado);
    } catch (error) {
      setStatus({ type: 'error', message: error?.message || 'No fue posible subir la portada.' });
    } finally {
      setUploadingCover(false);
    }
  };

  const handleUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    setUploading(true);
    setStatus({ type: '', message: '' });
    try {
      const created = await uploadModelMediaService(model.id, file);
      setMedia((prev) => [...prev, created]);
      setStatus({ type: 'success', message: 'Archivo publicado en el perfil.' });
    } catch (error) {
      setStatus({ type: 'error', message: error?.message || 'No fue posible subir el archivo.' });
    } finally {
      setUploading(false);
    }
  };

  const toggleVisibility = async (item) => {
    try {
      const updated = await updateModelMediaService(model.id, item.id, {
        is_public: !item.is_public,
      });
      setMedia((prev) => prev.map((m) => (m.id === item.id ? updated : m)));
    } catch (error) {
      setStatus({ type: 'error', message: error?.message || 'No fue posible actualizar el archivo.' });
    }
  };

  const removeMedia = async (item) => {
    if (!window.confirm('¿Eliminar este archivo del perfil público?')) return;
    try {
      await deleteModelMediaService(model.id, item.id);
      setMedia((prev) => prev.filter((m) => m.id !== item.id));
    } catch (error) {
      setStatus({ type: 'error', message: error?.message || 'No fue posible eliminar el archivo.' });
    }
  };

  if (!model) return null;

  return (
    <>
        <Nav variant="pills" className="profile-manager-tabs mb-4" activeKey={tab} onSelect={setTab}>
          <Nav.Item>
            <Nav.Link eventKey="profile">Datos</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="media">Galería ({media.length})</Nav.Link>
          </Nav.Item>
        </Nav>

        {status.message && (
          <div className={`alert ${status.type === 'success' ? 'alert-success' : 'alert-danger'} py-2 small`}>
            {status.message}
          </div>
        )}

        {tab === 'profile' && (
          <Form onSubmit={saveProfile} className="profile-manager-form">
            <label className={`cover-uploader ${uploadingCover ? 'is-busy' : ''}`}>
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleCover}
                disabled={uploadingCover}
              />
              {cover ? (
                <img src={asset(cover)} alt="Portada actual" />
              ) : (
                <span className="cover-uploader-empty">
                  <i className="fa-solid fa-panorama"></i>
                  Sin portada
                </span>
              )}
              <span className="cover-uploader-hint">
                <i className="fa-solid fa-camera me-2"></i>
                {uploadingCover ? 'Subiendo...' : 'Cambiar imagen de portada'}
              </span>
            </label>
            <p className="cover-uploader-help">
              Se muestra grande en la cabecera del perfil. Recomendado apaisada
              (1600×900 o mayor). Sin portada se usan las fotos de la galería.
            </p>

            <div className="row g-3">
              <div className="col-md-6">
                <Form.Label>Nombre artístico</Form.Label>
                <Form.Control
                  value={form.stage_name}
                  onChange={(e) => change('stage_name', e.target.value)}
                  maxLength={80}
                  placeholder="Cómo aparece en la web"
                />
              </div>
              <div className="col-md-6">
                <Form.Label>URL del perfil</Form.Label>
                <Form.Control
                  value={form.slug}
                  onChange={(e) => change('slug', e.target.value)}
                  maxLength={120}
                  placeholder="nombre-en-la-url"
                />
                <Form.Text className="text-muted">/modelo/{form.slug || '...'}</Form.Text>
              </div>
              <div className="col-12">
                <Form.Label>Frase de presentación</Form.Label>
                <Form.Control
                  value={form.tagline}
                  onChange={(e) => change('tagline', e.target.value)}
                  maxLength={160}
                  placeholder="Una línea que la describa"
                />
              </div>
              <div className="col-md-4">
                <Form.Label>Género</Form.Label>
                <Form.Select value={form.gender} onChange={(e) => change('gender', e.target.value)}>
                  <option value="">Sin especificar</option>
                  <option value="female">Mujer</option>
                  <option value="male">Hombre</option>
                  <option value="trans">Trans</option>
                  <option value="other">Otro</option>
                </Form.Select>
              </div>
              <div className="col-md-4">
                <Form.Label>Edad</Form.Label>
                <Form.Control
                  type="number"
                  min="18"
                  max="99"
                  value={form.age}
                  onChange={(e) => change('age', e.target.value)}
                />
              </div>
              <div className="col-md-4">
                <Form.Label>Idiomas</Form.Label>
                <Form.Control
                  value={form.languages}
                  onChange={(e) => change('languages', e.target.value)}
                  maxLength={120}
                  placeholder="Español, Inglés"
                />
              </div>
              <div className="col-md-6">
                <Form.Label>País</Form.Label>
                <Form.Control value={form.country} onChange={(e) => change('country', e.target.value)} maxLength={60} />
              </div>
              <div className="col-md-6">
                <Form.Label>Ciudad</Form.Label>
                <Form.Control value={form.city} onChange={(e) => change('city', e.target.value)} maxLength={60} />
              </div>
              <div className="col-12">
                <Form.Label>Sobre mí</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  value={form.description}
                  onChange={(e) => change('description', e.target.value)}
                  maxLength={5000}
                  placeholder="Presentación que verá el visitante"
                />
              </div>
              <div className="col-12">
                <Form.Label>Preferencias</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={form.preferences}
                  onChange={(e) => change('preferences', e.target.value)}
                  maxLength={2000}
                  placeholder="Gustos, temáticas, horarios preferidos..."
                />
              </div>
              <div className="col-12">
                <Form.Check
                  type="switch"
                  id="profile-visibility"
                  label="Mostrar este perfil en la vitrina pública"
                  checked={form.is_public}
                  onChange={(e) => change('is_public', e.target.checked)}
                />
              </div>
            </div>

            <div className="pt-4">
              <Button type="submit" className="w-100 btn-save-profile" disabled={saving}>
                {saving ? 'Guardando...' : 'Guardar perfil'}
              </Button>
            </div>
          </Form>
        )}

        {tab === 'media' && (
          <div className="profile-manager-media">
            <label className={`media-dropzone ${uploading ? 'is-busy' : ''}`}>
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp,video/mp4,video/webm,video/quicktime"
                onChange={handleUpload}
                disabled={uploading}
              />
              <i className="fa-solid fa-cloud-arrow-up"></i>
              <span>
                {uploading ? 'Subiendo...' : 'Agregar foto o video corto'}
              </span>
              <small>JPG, PNG, WEBP, MP4, WEBM o MOV · máx. 40 MB</small>
            </label>

            {media.length === 0 && (
              <p className="text-muted text-center mt-4 mb-0">
                Aún no hay material promocional en este perfil.
              </p>
            )}

            <div className="media-grid">
              {media.map((item) => (
                <figure className={`media-item ${item.is_public ? '' : 'is-hidden'}`} key={item.id}>
                  {item.type === 'video' ? (
                    <video src={asset(item.path)} preload="metadata" muted />
                  ) : (
                    <img src={asset(item.path)} alt={item.title || ''} loading="lazy" />
                  )}
                  <figcaption>
                    <span className="media-type">
                      <i className={`fa-solid ${item.type === 'video' ? 'fa-film' : 'fa-image'}`}></i>
                    </span>
                    <button
                      type="button"
                      title={item.is_public ? 'Ocultar del perfil' : 'Mostrar en el perfil'}
                      onClick={() => toggleVisibility(item)}
                    >
                      <i className={`fa-solid ${item.is_public ? 'fa-eye' : 'fa-eye-slash'}`}></i>
                    </button>
                    <button type="button" title="Eliminar" onClick={() => removeMedia(item)}>
                      <i className="fa-solid fa-trash-can"></i>
                    </button>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        )}
    </>
  );
}
